import { readContract, writeContract } from "@wagmi/core";
import { BSC_CHAIN_ID, wagmiConfig } from "./wagmi";
import { toBigInt } from "./checkout";

const REDEEM_ABI = [
  {
    type: "function",
    name: "previewRedeem",
    stateMutability: "view",
    inputs: [{ name: "pointsToBurn", type: "uint256" }],
    outputs: [
      { name: "grossTokenAmount", type: "uint256" },
      { name: "feeAmount", type: "uint256" },
      { name: "netTokenAmount", type: "uint256" },
      { name: "meetsMinimumWithdraw", type: "bool" },
    ],
  },
  {
    type: "function",
    name: "claimDigest",
    stateMutability: "view",
    inputs: [
      {
        name: "c",
        type: "tuple",
        components: [
          { name: "user", type: "address" },
          { name: "pointsToBurn", type: "uint256" },
          { name: "tokenAmount", type: "uint256" },
          { name: "deadline", type: "uint256" },
          { name: "claimId", type: "bytes32" },
        ],
      },
    ],
    outputs: [{ name: "", type: "bytes32" }],
  },
  {
    type: "function",
    name: "redeem",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "c",
        type: "tuple",
        components: [
          { name: "user", type: "address" },
          { name: "pointsToBurn", type: "uint256" },
          { name: "tokenAmount", type: "uint256" },
          { name: "deadline", type: "uint256" },
          { name: "claimId", type: "bytes32" },
        ],
      },
      { name: "signature", type: "bytes" },
    ],
    outputs: [],
  },
];

const formatError = (error) => {
  if (!error) return "Transaction failed";
  return error.shortMessage || error.message || "Transaction failed";
};

const buildClaim = (payload) => {
  if (!payload) throw new Error("Claim data is required");
  return {
    user: payload.user,
    pointsToBurn: toBigInt(payload.pointsToBurn, "pointsToBurn"),
    tokenAmount: toBigInt(payload.tokenAmount, "tokenAmount"),
    deadline: toBigInt(payload.deadline, "deadline"),
    claimId: payload.claimId,
  };
};

export const previewRedeem = async ({ pointsToBurn, contractAddress } = {}) => {
  try {
    const address =
      contractAddress || process.env.REACT_APP_REDEEM_CONTRACT_ADDRESS;
    if (!address) throw new Error("Redeem contract address is required");
    const value = toBigInt(pointsToBurn, "pointsToBurn");

    const result = await readContract(wagmiConfig, {
      address,
      abi: REDEEM_ABI,
      functionName: "previewRedeem",
      args: [value],
      chainId: BSC_CHAIN_ID,
    });

    return { success: true, data: result };
  } catch (error) {
    console.error("previewRedeem error:", error);
    return { success: false, error: formatError(error) };
  }
};

export const getClaimDigest = async ({ claim, contractAddress } = {}) => {
  try {
    const address =
      contractAddress || process.env.REACT_APP_REDEEM_CONTRACT_ADDRESS;
    if (!address) throw new Error("Redeem contract address is required");
    const normalizedClaim = buildClaim(claim);

    const digest = await readContract(wagmiConfig, {
      address,
      abi: REDEEM_ABI,
      functionName: "claimDigest",
      args: [normalizedClaim],
      chainId: BSC_CHAIN_ID,
    });

    return { success: true, data: { digest } };
  } catch (error) {
    console.error("getClaimDigest error:", error);
    return { success: false, error: formatError(error) };
  }
};

export const redeemSignedClaim = async (payload = {}) => {
  try {
    console.log("redeemSignedClaim payload:", payload);
    const { claim, signature } = payload || {};
    const address = process.env.REACT_APP_REDEEM_CONTRACT_ADDRESS;
    if (!address) throw new Error("Redeem contract address is required");
    const sig = signature || payload?.signature;
    if (!sig) throw new Error("Signature is required");

    const normalizedClaim = buildClaim(claim ?? payload);

    const txId = await writeContract(wagmiConfig, {
      address,
      abi: REDEEM_ABI,
      functionName: "redeem",
      args: [normalizedClaim, sig],
      chainId: BSC_CHAIN_ID,
    });

    return { success: true, data: { txId } };
  } catch (error) {
    console.error("redeemSignedClaim error:", error);
    return { success: false, error: formatError(error) };
  }
};

export const __abis = {
  REDEEM_ABI,
};
